import { Introductions, MyProjects, Users } from '@platform/client';
import {
  AccountTypeEnum,
  RepCard,
  ProCard,
  RepProjectPanelProject,
  ProCardProject,
} from '@platform/client/schemas';
import { Modal, ModalCallToAction, Spinner } from '@platform/components';
import { useQueryClient } from '@tanstack/react-query';
import { useState, useContext, createContext, useCallback } from 'react';
import ConnectedIntroductionModalContent from './ConnectedIntroductionModalContent';
import DeclinedIntroductionModalContent from './DeclinedIntroductionModalContent';
import ProDecliningIntroductionModalContent from './ProDecliningIntroductionModalContent';
import { ProIntroductionModalContent } from './ProIntroductionModalContent';
import RepDecliningIntroductionModalContent from './RepDecliningIntroductionModalContent';
import { RepIntroductionModalContent } from './RepIntroductionModalContent';
import { navigate } from 'wouter/use-browser-location';

export enum IntroductionModalStage {
  Introduction = 'introduction',
  Submitting = 'submitting',
  Connected = 'connected',
  Declining = 'declining',
  Declined = 'declined',
}
/**
 * TODO: Make reasons in the database make sense
 */
// TODO: Do we need to redo these as part of m3? At the very least these need to be
// string vals to avoid a type error on RadioGroup
export enum DeclineReasons {
  Different_Choice = '101',
  Wrong_Skills = '102',
  Timezone_Difference = '106',
  Project_Done = '111',
  Rep_User_Disable = '17',
  Pro_User_Disable = '112',
  Other = '14',
}

export type MyProjectsModalProps =
  | {
      userType: typeof AccountTypeEnum.organization;
      cardData: RepCard;
      project?: RepProjectPanelProject;
    }
  | {
      userType: typeof AccountTypeEnum.experteer;
      cardData: ProCard;
      project?: ProCardProject;
    };

interface IntroductionModalContext {
  isOpen: boolean;
  openIntroductionModal: (props: MyProjectsModalProps) => void;
  closeIntroductionModal: () => void;
  modalStage: IntroductionModalStage;
  setModalStage: (modalStage: IntroductionModalStage) => void;
  modalProps?: MyProjectsModalProps;
}

export const IntroductionModalContext = createContext<IntroductionModalContext>({
  isOpen: false,
  openIntroductionModal: () => {},
  closeIntroductionModal: () => {},
  modalStage: IntroductionModalStage.Introduction,
  setModalStage: () => {},
});

export const IntroductionModalProvider = ({ children }: { children: React.ReactNode }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [modalStage, setModalStage] = useState(IntroductionModalStage.Introduction);
  const [modalProps, setModalProps] = useState<MyProjectsModalProps | undefined>(undefined);

  const openIntroductionModal = useCallback((props: MyProjectsModalProps) => {
    setModalProps(props);
    setModalStage(IntroductionModalStage.Introduction);
    setIsOpen(true);
  }, []);

  const closeIntroductionModal = () => {
    setModalStage(IntroductionModalStage.Introduction);
    setModalProps(undefined);
    const url = new URL(window.location.href);
    url.searchParams.delete('introductionId');
    window.history.pushState({}, '', url.toString());
    setIsOpen(false);
  };

  return (
    <IntroductionModalContext.Provider
      value={{
        isOpen,
        openIntroductionModal,
        closeIntroductionModal,
        modalStage,
        setModalStage,
        modalProps,
      }}
    >
      {children}
      <IntroductionModal />
    </IntroductionModalContext.Provider>
  );
};

const IntroductionModal = () => {
  const user = Users.useGetCurrentUser();

  const [declineReason, setDeclineReason] = useState<DeclineReasons | undefined>();
  const [additionalDetails, setAdditionalDetails] = useState('');
  // On successful connection, we return the discovery id and store it here to navigate to project hub on confirm CTA
  const [assignmentDiscoveryId, setAssignmentDiscoveryId] = useState<number | undefined>(undefined);

  const { isOpen, closeIntroductionModal, modalStage, setModalStage, modalProps } =
    useContext(IntroductionModalContext);

  const queryClient = useQueryClient();
  const connectIntroduction = Introductions.useUpdateStatus({
    mutation: {
      onSuccess: (data) => {
        queryClient.invalidateQueries({ queryKey: MyProjects.getGetProUserMyProjectsQueryKey() });
        queryClient.invalidateQueries({ queryKey: MyProjects.getGetOrgUserMyProjectsQueryKey() });
        queryClient.invalidateQueries({ queryKey: MyProjects.getCountUserActionItemsQueryKey() });
        if (data.assignmentDiscoveryId) {
          setAssignmentDiscoveryId(data.assignmentDiscoveryId);
          setModalStage(IntroductionModalStage.Connected);
        }
      },
    },
  });

  if (!user.data || !modalStage || !modalProps) {
    return null;
  }

  const { userType, cardData, project } = modalProps;
  const { introduction } = cardData;
  const isRepresentative = userType === 'organization';

  const userInitiatedIntro =
    (introduction.initiationType === 'Experteer' && !isRepresentative) ||
    (introduction.initiationType === 'Organization' && isRepresentative);

  let primaryCTA: ModalCallToAction | undefined = {
    text: 'Connect',
    callback: () => {
      connectIntroduction.mutate({
        data: {
          status: 'mutual-intro-one',
          introductionId: introduction.id,
        },
      });
      setModalStage(IntroductionModalStage.Submitting);
    },
  };

  let secondaryCTA: ModalCallToAction | undefined = {
    text: 'Decline',
    callback: () => {
      setModalStage(IntroductionModalStage.Declining);
    },
  };

  let title = userInitiatedIntro ? 'Sent introduction' : 'Someone wants to work with you!';

  if (
    introduction.outcome === 'accepted' ||
    introduction.outcome === 'declined' ||
    userInitiatedIntro
  ) {
    primaryCTA = {
      text: 'Close',
      callback: () => {
        closeIntroductionModal();
      },
    };
    secondaryCTA = undefined;
  } else if (modalStage === IntroductionModalStage.Submitting) {
    primaryCTA = undefined;
    secondaryCTA = undefined;
    title = 'Updating introduction';
  } else if (modalStage === IntroductionModalStage.Connected) {
    primaryCTA = {
      text: 'See instructions',
      callback: () => {
        navigate(`platform/project-hub/${assignmentDiscoveryId}`);
      },
    };
    secondaryCTA = {
      text: 'Close',
      callback: () => {
        closeIntroductionModal();
      },
    };
    title = "You're connected!";
  } else if (modalStage === IntroductionModalStage.Declining) {
    primaryCTA = {
      text: 'Save & continue',
      callback: () => {
        if (declineReason === DeclineReasons.Project_Done) {
          //TODO: POST to disable project
        }
        if (declineReason === DeclineReasons.Other) {
          if (additionalDetails) {
            setModalStage(IntroductionModalStage.Declined);
          }
        } else if (declineReason) {
          connectIntroduction.mutate({
            data: {
              status: 'rejected',
              introductionId: introduction.id,
              reasonId: Number(declineReason),
            },
          });
          setModalStage(IntroductionModalStage.Declined);
        }
      },
    };
    secondaryCTA = {
      text: 'Go back',
      callback: () => {
        setModalStage(IntroductionModalStage.Introduction);
      },
    };
    title = 'Decline Introduction';
  } else if (modalStage === IntroductionModalStage.Declined) {
    primaryCTA = {
      text: 'Close',
      callback: () => {
        closeIntroductionModal();
      },
    };
    secondaryCTA = undefined;
    title = 'Introduction declined';
  }

  const connectedWithContentProps = {
    connectedWith:
      isRepresentative ?
        `${cardData.professional.firstname} ${cardData.professional.lastname}`
      : `${cardData.organization.name}`,
    projectName: project?.name,
  };

  return (
    <Modal
      isOpen={isOpen}
      headerDivision='divider'
      onClose={closeIntroductionModal}
      variant='dialog'
      title={title}
      primaryCTA={primaryCTA}
      secondaryCTA={secondaryCTA}
      ignoreExtraPadding
    >
      {modalStage === IntroductionModalStage.Declining && !isRepresentative && (
        <ProDecliningIntroductionModalContent
          declineReason={declineReason}
          additionalDetails={additionalDetails}
          setAdditionalDetails={setAdditionalDetails}
          setDeclineReason={setDeclineReason}
        />
      )}
      {modalStage === IntroductionModalStage.Declining && isRepresentative && (
        <RepDecliningIntroductionModalContent
          declineReason={declineReason}
          additionalDetails={additionalDetails}
          setAdditionalDetails={setAdditionalDetails}
          setDeclineReason={setDeclineReason}
        />
      )}
      {modalStage === IntroductionModalStage.Submitting && (
        <div className='flex items-center justify-center'>
          <Spinner />
        </div>
      )}
      {modalStage === IntroductionModalStage.Connected && (
        <ConnectedIntroductionModalContent {...connectedWithContentProps} />
      )}
      {modalStage === IntroductionModalStage.Declined && <DeclinedIntroductionModalContent />}
      {modalStage === IntroductionModalStage.Introduction && isRepresentative && (
        <RepIntroductionModalContent {...modalProps} />
      )}
      {modalStage === IntroductionModalStage.Introduction && !isRepresentative && (
        <ProIntroductionModalContent {...modalProps} />
      )}
    </Modal>
  );
};

export default IntroductionModal;
